import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { zodResolver } from '@hookform/resolvers/zod';
import { useJitsu } from '@jitsu/jitsu-react';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import {
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@tg-web/components';

import {
  OjGoogleAccount,
  useActivateGoogleAccountV2,
} from '../../../shared/api';
import { GoogleAnalytics } from '../../../shared/model/analytics';
import { ViewField } from '../../../shared/ui/ViewField';

const FormSchema = z.object({
  items: z.array(z.string()),
});

export type CalendarsSynchronizationFormProps = {
  googleAccount: OjGoogleAccount;
  onSuccess?: VoidFunction;
};

export function CalendarsSynchronizationForm({
  googleAccount,
  onSuccess,
}: CalendarsSynchronizationFormProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { analytics } = useJitsu();

  const form = useForm<z.infer<typeof FormSchema>>({
    defaultValues: {
      items: googleAccount.google_calendars
        ?.filter((it) => it.active)
        .map((it) => it.id),
    },
    resolver: zodResolver(FormSchema),
  });

  const googleAccountCalendars = useActivateGoogleAccountV2({
    onSuccess: () => {
      queryClient.removeQueries({
        queryKey: ['events'],
      });
      queryClient.invalidateQueries({
        queryKey: ['v2', 'google_accounts'],
      });

      onSuccess?.();
    },
  });

  function onSubmit(data: z.infer<typeof FormSchema>) {
    analytics.track(GoogleAnalytics.CALENDARS_SYNC_SUBMIT, {
      selectedCount: data.items.length,
      totalCount: googleAccount.google_calendars?.length ?? 0,
    });
    googleAccountCalendars.mutate({
      body: { google_account: { calendar_ids: data.items } },
      pathParams: { id: googleAccount.id },
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="space-y-3">
          <p className="typo-text-bold">
            {t('all:google.calendar_sync.import.title')}
          </p>
          <div>
            <FormField
              render={() => (
                <FormItem withoutWrapper>
                  {googleAccount.google_calendars?.map((it) => (
                    <FormField
                      render={({ field }) => {
                        return (
                          <FormItem key={it.id} withoutWrapper>
                            <ViewField
                              leftIcon={
                                <div className="flex h-[32px] w-[32px] flex-col items-center justify-center">
                                  <FormControl>
                                    <Checkbox
                                      disabled={
                                        googleAccountCalendars.isPending
                                      }
                                      onCheckedChange={(checked) => {
                                        return checked
                                          ? field.onChange([
                                              ...field.value,
                                              it.id,
                                            ])
                                          : field.onChange(
                                              field.value?.filter(
                                                (value) => value !== it.id,
                                              ),
                                            );
                                      }}
                                      checked={field.value?.includes(it.id)}
                                    />
                                  </FormControl>
                                </div>
                              }
                            >
                              <FormLabel className="typo-text">
                                {it.name}
                              </FormLabel>
                            </ViewField>
                          </FormItem>
                        );
                      }}
                      control={form.control}
                      key={it.id}
                      name="items"
                    />
                  ))}
                  <FormMessage />
                </FormItem>
              )}
              control={form.control}
              name="items"
            />
          </div>
        </div>
        <Button
          className="mt-6"
          isLoading={googleAccountCalendars.isPending}
          type="submit"
        >
          {t('all:common.buttons.save')}
        </Button>
      </form>
    </Form>
  );
}
